import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  AlertTriangle,
  Bell,
  MessageSquare,
  X,
  Check,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/panic', icon: AlertTriangle, label: 'Panic', highlight: true },
  { path: '/alerts', icon: Bell, label: 'Alerts' },
  { path: '/chat', icon: MessageSquare, label: 'Chat' },
];

const PRESET_POSITIONS = {
  bottom: 'bottom-0 left-0 right-0',
  top: 'top-14 left-0 right-0',
  left: 'left-0 top-1/2 -translate-y-1/2',
  right: 'right-0 top-1/2 -translate-y-1/2',
};

const MobileNavigation = ({ activeAlerts }) => {
  const location = useLocation();
  const longPressTimer = useRef(null);
  
  const [editMode, setEditMode] = useState(false);
  const [menuPosition, setMenuPosition] = useState('bottom');
  const [isLoading, setIsLoading] = useState(true);

  const visibleNav = NAV_ITEMS;

  useEffect(() => {
    fetchMenuPosition();
  }, []);

  const fetchMenuPosition = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/menu-position`);
      setMenuPosition(response.data.preset || 'bottom');
    } catch (error) {
      console.error('Failed to fetch menu position:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMenuPosition = async (preset) => {
    try {
      await axios.put(`${API_URL}/settings/menu-position`, {
        preset,
        x: null,
        y: null,
        is_custom: false
      });
      setMenuPosition(preset);
      toast.success('Menu position saved');
    } catch (error) {
      toast.error('Failed to save menu position');
    }
  };

  const resetMenuPosition = async () => {
    try {
      await axios.post(`${API_URL}/settings/menu-position/reset`);
      setMenuPosition('bottom');
      toast.success('Menu position reset');
    } catch (error) {
      toast.error('Failed to reset menu position');
    }
  };

  // Long press detection
  const handleTouchStart = useCallback(() => {
    if (editMode) return;
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      toast.info('Select a position for the menu');
    }, 800);
  }, [editMode]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  }, []);

  const handleMouseDown = useCallback(() => {
    if (editMode) return;
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      toast.info('Select a position for the menu');
    }, 800);
  }, [editMode]);

  const handleMouseUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isVertical = menuPosition === 'left' || menuPosition === 'right';

  if (isLoading) return null;

  const getNavClasses = () => {
    const base = 'bg-panel border-white/10';
    if (isVertical) {
      return `${base} border-r flex flex-col py-4 px-2 rounded-r-lg`;
    }
    return `${base} border-t pb-safe`;
  };

  return (
    <>
      {/* Edit Mode Overlay */}
      {editMode && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setEditMode(false)} />
      )}

      {/* Edit Mode Controls */}
      {editMode && (
        <div className="fixed top-20 left-4 right-4 z-[60] bg-panel border border-white/10 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-chivo font-bold text-sm uppercase text-white">Position Menu</h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditMode(false)}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Preset Buttons */}
          <div className="grid grid-cols-4 gap-2">
            {Object.keys(PRESET_POSITIONS).map((preset) => (
              <Button
                key={preset}
                size="sm"
                variant={menuPosition === preset ? 'default' : 'outline'}
                onClick={() => saveMenuPosition(preset)}
                className="capitalize text-xs"
              >
                {preset}
              </Button>
            ))}
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={resetMenuPosition}
              className="flex-1"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              size="sm"
              onClick={() => setEditMode(false)}
              className="flex-1"
            >
              <Check className="w-4 h-4 mr-2" />
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav
        className={`lg:hidden fixed z-50 ${PRESET_POSITIONS[menuPosition]} ${editMode ? 'ring-2 ring-primary ring-offset-2 ring-offset-black' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className={getNavClasses()}>
          <div className={`flex ${isVertical ? 'flex-col gap-2' : 'items-center justify-around py-2'}`}>
            {visibleNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              if (editMode) {
                return (
                  <div
                    key={item.path}
                    className={`flex ${isVertical ? 'flex-row items-center gap-2 px-2 py-2' : 'flex-col items-center gap-1 p-2'} ${
                      active
                        ? 'text-primary'
                        : item.highlight
                          ? 'text-alert-red'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {item.path === '/panic' ? (
                      <div className={`${isVertical ? 'w-10 h-10' : 'w-12 h-12 -mt-4'} rounded-full bg-alert-red flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.5)]`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Icon className="w-5 h-5" />
                          {item.path === '/alerts' && activeAlerts > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-alert-red rounded-full text-[8px] font-bold text-white flex items-center justify-center">
                              {activeAlerts}
                            </span>
                          )}
                        </div>
                        {!isVertical && (
                          <span className="font-mono text-[10px]">{item.label}</span>
                        )}
                      </>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex ${isVertical ? 'flex-row items-center gap-2 px-2 py-2' : 'flex-col items-center gap-1 p-2'} ${
                    active
                      ? 'text-primary'
                      : item.highlight
                        ? 'text-alert-red'
                        : 'text-muted-foreground'
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.path === '/panic' ? (
                    <div className={`${isVertical ? 'w-10 h-10' : 'w-12 h-12 -mt-4'} rounded-full bg-alert-red flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.5)]`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Icon className="w-5 h-5" />
                        {item.path === '/alerts' && activeAlerts > 0 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-alert-red rounded-full text-[8px] font-bold text-white flex items-center justify-center">
                            {activeAlerts}
                          </span>
                        )}
                      </div>
                      {!isVertical && (
                        <span className="font-mono text-[10px]">{item.label}</span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default MobileNavigation;
